/**
 * MasterDataDocDispatchFormDialog — Create/Edit `docs_dispatch`.
 * 1:1 port of Access `frmMdQryDocsDispatchAdd`. Compact Sales Contract style.
 *
 * Single-column (Access all inputs Left=2640), field order by Top:
 *   Code → Pic → Company Name → Tel → Location → Email → Address → Note.
 * `code` là PK text: required, disable khi edit. VBA: code trống → "Please type
 * Code!"; code trùng (create) → "Code is existed!" (BE raise → snackbar).
 */
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useEffect, useRef, useState } from 'react'

import { useSnackbar } from '@/hooks/useSnackbar'
import {
  useCreateMdDocDispatchMutation,
  useUpdateMdDocDispatchMutation,
  useLazySuggestMdDocDispatchCodeQuery,
  useLazyCheckMdDocDispatchCodeQuery,
} from '@/store/api/masterDataCrudApi'
import type { MasterDataDocDispatch } from '@/types/api'
import { getErrorMessage } from '@/utils/error'

import { compactDialogContentSx, dialogHeaderBtnSx, dialogTitleActionsSx, dialogTitleSx, dialogTitleTextSx, roSx } from '../purchase/dialogStyle'

export interface Props {
  open: boolean
  mode: 'create' | 'edit'
  initialValue?: MasterDataDocDispatch | null
  onClose: () => void
  onSaved?: () => void
}

type FormState = {
  code: string
  pic: string
  company_name: string
  tel: string
  location: string
  email: string
  address: string
  note: string
}

const EMPTY: FormState = {
  code: '',
  pic: '',
  company_name: '',
  tel: '',
  location: '',
  email: '',
  address: '',
  note: '',
}

export default function MasterDataDocDispatchFormDialog({
  open,
  mode,
  initialValue,
  onClose,
  onSaved,
}: Props) {
  const snack = useSnackbar()
  const isEdit = mode === 'edit'

  const [createDoc, { isLoading: creating }] = useCreateMdDocDispatchMutation()
  const [updateDoc, { isLoading: updating }] = useUpdateMdDocDispatchMutation()
  const loading = creating || updating

  // access-suggest: gợi ý mã `code` (SD-<n>) khi New + re-suggest khi trùng.
  const [triggerSuggest, suggestState] = useLazySuggestMdDocDispatchCodeQuery()
  const [triggerCheck] = useLazyCheckMdDocDispatchCodeQuery()
  const [regenNotice, setRegenNotice] = useState<{ from: string; to: string } | null>(null)
  const [precheckBusy, setPrecheckBusy] = useState(false)
  const precheckBusyRef = useRef(false)

  const [form, setForm] = useState<FormState>(EMPTY)
  const [showErrors, setShowErrors] = useState(false)

  // Hydrate CHỈ depend [open, mode, row] — KHÔNG list/suggest (anti-pattern #4).
  useEffect(() => {
    if (!open) return
    setShowErrors(false)
    setRegenNotice(null)
    setPrecheckBusy(false)
    precheckBusyRef.current = false
    if (isEdit && initialValue) {
      setForm({
        code: initialValue.code ?? '',
        pic: initialValue.pic ?? '',
        company_name: initialValue.company_name ?? '',
        tel: initialValue.tel ?? '',
        location: initialValue.location ?? '',
        email: initialValue.email ?? '',
        address: initialValue.address ?? '',
        note: initialValue.note ?? '',
      })
    } else {
      setForm(EMPTY)
    }
  }, [open, isEdit, initialValue])

  // Auto-suggest mã khi mở New (lazy only — không auto-save, chỉ điền field).
  useEffect(() => {
    if (!open || isEdit) return
    let cancelled = false
    void triggerSuggest(undefined, false).unwrap().then((res) => {
      if (cancelled) return
      const newVal = (res.code || '').trim()
      if (!newVal) return
      setForm((prev) => (prev.code.trim() ? prev : { ...prev, code: newVal }))
    }).catch(() => {})
    return () => { cancelled = true }
  }, [open, isEdit, triggerSuggest])

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) =>
    setForm((p) => ({ ...p, [k]: v }))

  // Dismiss alert khi user sửa code khác mã vừa suggest.
  useEffect(() => {
    if (regenNotice && form.code !== regenNotice.to) setRegenNotice(null)
  }, [form.code, regenNotice])

  const codeEmpty = !form.code.trim()
  const canSave = !codeEmpty && !loading

  const handleClose = () => {
    if (loading) return
    onClose()
  }

  // Blur field Code (New): check trùng → nếu trùng, re-suggest mã mới + alert.
  const handleBlur = async () => {
    if (isEdit) return
    const val = form.code.trim()
    if (!val) return
    precheckBusyRef.current = true
    setPrecheckBusy(true)
    try {
      const check = await triggerCheck(val).unwrap()
      if (!check.exists) return
      const next = await triggerSuggest(undefined, false).unwrap()
      const newVal = (next.code || '').trim()
      if (newVal && newVal !== val) {
        setForm((prev) => ({ ...prev, code: newVal }))
        setRegenNotice({ from: val, to: newVal })
      }
    } catch {
      // BE sẽ raise lúc Save nếu thật trùng — không poison UX.
    } finally {
      precheckBusyRef.current = false
      setPrecheckBusy(false)
    }
  }

  const buildBody = () => ({
    company_name: form.company_name.trim() || null,
    location: form.location.trim() || null,
    address: form.address.trim() || null,
    pic: form.pic.trim() || null,
    tel: form.tel.trim() || null,
    email: form.email.trim() || null,
    note: form.note.trim() || null,
  })

  const handleSave = async () => {
    if (precheckBusyRef.current) return // race guard: blur đang check dở
    if (codeEmpty) {
      setShowErrors(true)
      return
    }
    try {
      if (isEdit && initialValue) {
        await updateDoc({ code: initialValue.code, body: buildBody() }).unwrap()
        snack.success('Doc dispatch updated')
      } else {
        await createDoc({ code: form.code.trim(), ...buildBody() }).unwrap()
        snack.success('Doc dispatch created')
      }
      onSaved?.()
      onClose()
    } catch (e) {
      const msg = getErrorMessage(e, isEdit ? 'Failed to update' : 'Failed to create')
      // Backup khi race lọt qua blur-check: BE raise trùng → tự re-suggest mã mới.
      const lower = msg.toLowerCase()
      const isDup = !isEdit && (
        lower.includes('exist') || lower.includes('duplicate') || lower.includes('tồn tại')
      )
      if (isDup) {
        try {
          const next = await triggerSuggest(undefined, false).unwrap()
          const newVal = (next.code || '').trim()
          const oldVal = form.code.trim()
          if (newVal && newVal !== oldVal) {
            setForm((prev) => ({ ...prev, code: newVal }))
            setRegenNotice({ from: oldVal, to: newVal })
            return // alert vàng đủ báo — không snack error
          }
        } catch { /* fallthrough → snack error */ }
      }
      snack.error(msg)
    }
  }

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        if (loading) return
        if (reason === 'backdropClick') return
        handleClose()
      }}
      maxWidth="sm"
      fullWidth
      scroll="paper"
    >
      <DialogTitle sx={dialogTitleSx}>
        <Typography component="span" noWrap sx={dialogTitleTextSx}>
          {isEdit ? 'Edit Doc Dispatch' : 'New Doc Dispatch'}
          {isEdit && initialValue ? (
            <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
              {initialValue.code}
            </Typography>
          ) : null}
        </Typography>
        <Stack direction="row" spacing={0.5} sx={dialogTitleActionsSx}>
          <Button
            type="button"
            variant="contained"
            size="small"
            color="success"
            onClick={() => void handleSave()}
            disabled={!canSave}
            sx={dialogHeaderBtnSx}
          >
            {loading ? <CircularProgress size={14} color="inherit" /> : 'Save'}
          </Button>
          <Button type="button" variant="outlined" size="small" onClick={handleClose} disabled={loading} sx={dialogHeaderBtnSx}>
            Cancel
          </Button>
        </Stack>
      </DialogTitle>
      <Divider />
      <DialogContent dividers sx={compactDialogContentSx}>
        {regenNotice ? (
          <Alert severity="warning" sx={{ mb: 1.25, py: 0.25 }}>
            Code <b>{regenNotice.from}</b> was just created — suggested new code{' '}
            <b>{regenNotice.to}</b>.
          </Alert>
        ) : null}
        <Grid container spacing={1.5} sx={{ mt: 0 }}>
          <Grid item xs={12}>
            <TextField
              label="Code"
              size="small"
              fullWidth
              required
              disabled={isEdit}
              error={showErrors && codeEmpty}
              value={form.code}
              onChange={(e) => set('code', e.target.value)}
              onBlur={() => void handleBlur()}
              InputLabelProps={{ shrink: true }}
              inputProps={{ tabIndex: isEdit ? -1 : 1 }}
              helperText={
                isEdit
                  ? 'Code cannot be changed after creation.'
                  : precheckBusy
                    ? 'Verifying code…'
                    : suggestState.isFetching
                      ? 'Suggesting next code…'
                      : 'Suggested code below; edit if needed.'
              }
              sx={isEdit ? roSx : undefined}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Pic"
              size="small"
              fullWidth
              value={form.pic}
              onChange={(e) => set('pic', e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ tabIndex: 2 }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Company Name"
              size="small"
              fullWidth
              value={form.company_name}
              onChange={(e) => set('company_name', e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ tabIndex: 3 }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Tel"
              size="small"
              fullWidth
              value={form.tel}
              onChange={(e) => set('tel', e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ tabIndex: 4 }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Location"
              size="small"
              fullWidth
              value={form.location}
              onChange={(e) => set('location', e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ tabIndex: 5 }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Email"
              size="small"
              fullWidth
              value={form.email}
              onChange={(e) => set('email', e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ tabIndex: 6 }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Address"
              size="small"
              fullWidth
              value={form.address}
              onChange={(e) => set('address', e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ tabIndex: 7 }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Note"
              size="small"
              fullWidth
              multiline
              minRows={3}
              value={form.note}
              onChange={(e) => set('note', e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ tabIndex: 8 }}
            />
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  )
}
